import React, { useEffect, useRef, useState } from 'react';

import { CountrySelect, InputWithLabel } from 'main/components';
import { PRODUCT_OPTIONS_SHORTER } from 'main/consts';

export const FullContactForm = () => {
  const formRef = useRef<HTMLFormElement>(null);

  // UI State
  const [selectedElement, setSelectedElement] = useState<null | string>(null);
  const [windowWidth, setWindowWidth] = useState<number>(
    typeof window !== 'undefined' ? window.innerWidth : 0,
  );
  const isMobile = windowWidth <= 991;

  // Form Data State
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [country, setCountry] = useState<string>('');
  const [company, setCompany] = useState<string>('');

  // Products State (Array of IDs)
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [radioOption, setRadioOption] = useState<'custom' | 'all'>('custom');

  // Submission State
  const [errors, setErrors] = useState<{ [key: string]: string } | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<boolean>(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  // --- LOGIC: Handle Checkboxes ---
  const handleCheckboxChange = (id: string) => {
    let newSelection: string[];

    if (selectedProductIds.includes(id)) {
      newSelection = selectedProductIds.filter(item => item !== id);
    } else {
      newSelection = [...selectedProductIds, id];
    }

    setSelectedProductIds(newSelection);

    // Update Radio Button State
    const allSelected = PRODUCT_OPTIONS_SHORTER.every(opt =>
      newSelection.includes(opt.id),
    );
    setRadioOption(allSelected ? 'all' : 'custom');
  };

  const handleRadioChange = (value: 'custom' | 'all') => {
    setRadioOption(value);
    if (value === 'all') {
      setSelectedProductIds(PRODUCT_OPTIONS_SHORTER.map(opt => opt.id));
    } else {
      setSelectedProductIds([]);
    }
  };

  // --- LOGIC: Turnstile Script ---
  useEffect(() => {
    (window as any).onTurnstileSuccess = (token: string) => {
      setTurnstileToken(token);
      setErrors(prev => (prev ? { ...prev, captcha: '' } : null));
    };

    const scriptId = 'cf-turnstile-script';
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
    }

    return () => {
      delete (window as any).onTurnstileSuccess;
    };
  }, []);

  // --- LOGIC: Resize ---
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // --- LOGIC: Submit ---
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: { [key: string]: string } = {};

    if (!email.trim()) {
      newErrors.email = 'Email field is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Value is not a valid email format';
    }

    // Validate Turnstile
    if (!turnstileToken) {
      newErrors.captcha = 'Please complete the security check';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      setLoading(true);
      setSubmitError(false);

      try {
        if (formRef.current) {
          const formData = new FormData(formRef.current);

          const response = await fetch(
            'https://salesforce-form-proxy.orqa.workers.dev',
            {
              method: 'POST',
              body: formData,
            },
          );

          if (response.ok) {
            setIsSubmitted(true);
            setErrors(null);

            // Optional: Reset logic if needed
            if ((window as any).turnstile) (window as any).turnstile.reset();
          } else {
            setSubmitError(true);
          }
        }
      } catch (error) {
        setSubmitError(true);
      } finally {
        setLoading(false);
      }
    }
  };

  // Helper to split products into two columns
  const midPoint = Math.ceil(PRODUCT_OPTIONS_SHORTER.length / 2);
  const firstColProducts = PRODUCT_OPTIONS_SHORTER.slice(0, midPoint);
  const secondColProducts = PRODUCT_OPTIONS_SHORTER.slice(midPoint);

  return (
    <div className="auvsi-contact-main-section">
      <section className="auvsi-contact-section">
        <div className="auvsi-contact-content">
          <h2 className="auvsi-contact-title">Get in touch</h2>
          <p className="auvsi-contact-description">
            Connect with us. For assistance with products or support inquiries,
            reach out directly. Our team is ready to help you achieve peak FPV
            performance.
          </p>

          <form
            id="asda-form"
            ref={formRef}
            className="auvsi-contact-form"
            onSubmit={handleSubmit}
          >
            {/* --- HIDDEN SALESFORCE FIELDS --- */}
            <input type="hidden" name="oid" value="00Dfn000006LhOH" />
            <input type="hidden" name="retURL" value="https://orqafpv.com" />
            <input type="hidden" name="lead_source" value="Website" />

            <div className="asda__form-wrapper">
              <div className="auvsi-form-columns-container">
                {/* --- LEFT COLUMN: Personal Info --- */}
                <div className="auvsi-form-column form-column-left">
                  <p className="auvsi-column-description">
                    Contact Information
                  </p>

                  <div className="auvsi-input-fields-container">
                    <InputWithLabel
                      setter={setFirstName}
                      setSelectedElement={setSelectedElement}
                      selectedElement={selectedElement}
                      name={'first_name'}
                      label={'First Name'}
                      errors={errors}
                      getter={firstName}
                    />

                    <InputWithLabel
                      setter={setLastName}
                      setSelectedElement={setSelectedElement}
                      selectedElement={selectedElement}
                      name={'last_name'}
                      label={'Last Name'}
                      errors={errors}
                      getter={lastName}
                    />

                    <InputWithLabel
                      required
                      setSelectedElement={setSelectedElement}
                      selectedElement={selectedElement}
                      name={'email'}
                      label={'Email'}
                      setter={setEmail}
                      errors={errors}
                      getter={email || ''}
                    />

                    <CountrySelect
                      value={country}
                      required={false}
                      onChange={setCountry}
                      error={errors?.country_code}
                      name="country_code"
                    />

                    <InputWithLabel
                      setSelectedElement={setSelectedElement}
                      selectedElement={selectedElement}
                      name={'company'}
                      label={'Organization'}
                      setter={setCompany}
                      getter={company}
                    />
                  </div>
                </div>

                {/* --- RIGHT COLUMN: Product Checkboxes --- */}
                <div className="auvsi-form-column form-column-right">
                  <p className="auvsi-column-description">
                    Select your interests
                  </p>

                  <div className="auvsi-radio-options">
                    <div className="auvsi-radio-option">
                      <input
                        type="radio"
                        id="customOption"
                        name="requestType" // Not sent to Salesforce
                        checked={radioOption === 'custom'}
                        onChange={() => handleRadioChange('custom')}
                      />
                      <label htmlFor="customOption">Custom selection</label>
                    </div>
                    <div className="auvsi-radio-option">
                      <input
                        type="radio"
                        id="allOption"
                        name="requestType" // Not sent to Salesforce
                        checked={radioOption === 'all'}
                        onChange={() => handleRadioChange('all')}
                      />
                      <label htmlFor="allOption">All options</label>
                    </div>
                  </div>

                  <div className="auvsi-checkbox-section">
                    {/* HIDDEN SELECT: Maps selected checkboxes to Salesforce field */}
                    <select
                      multiple
                      name="00Ncv0000073ii3"
                      style={{ display: 'none' }}
                      value={selectedProductIds}
                      onChange={() => null}
                    >
                      {PRODUCT_OPTIONS_SHORTER.map(opt => (
                        <option key={opt.id} value={opt.id}>
                          {opt.label}
                        </option>
                      ))}
                    </select>

                    <div className="auvsi-checkbox-columns">
                      <div className="auvsi-checkbox-column">
                        {firstColProducts.map(option => (
                          <div
                            key={option.id}
                            className="auvsi-checkbox-option"
                          >
                            <input
                              type="checkbox"
                              id={option.id}
                              checked={selectedProductIds.includes(option.id)}
                              onChange={() => handleCheckboxChange(option.id)}
                            />
                            <label htmlFor={option.id}>{option.label}</label>
                          </div>
                        ))}
                      </div>
                      <div className="auvsi-checkbox-column">
                        {secondColProducts.map(option => (
                          <div
                            key={option.id}
                            className="auvsi-checkbox-option"
                          >
                            <input
                              type="checkbox"
                              id={option.id}
                              checked={selectedProductIds.includes(option.id)}
                              onChange={() => handleCheckboxChange(option.id)}
                            />
                            <label htmlFor={option.id}>{option.label}</label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* --- SUBMIT SECTION (Message Left, Button Right) --- */}
            <div
              style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: 'center',
                marginTop: '10px',
                width: '100%',
                gap: isMobile && (isSubmitted || submitError) ? '15px' : '0',
              }}
            >
              {/* STATUS MESSAGE AREA: Success OR Error */}
              {(isSubmitted || submitError) && (
                <div
                  style={{
                    textAlign: 'left',
                    width: isMobile ? '100%' : 'auto',
                    marginBottom: isMobile ? '16px' : '',
                  }}
                >
                  {isSubmitted && !submitError && (
                    <p
                      style={{
                        fontSize: '14px',
                        color: '#07bc0c',
                        margin: 0,
                      }}
                    >
                      Form successfully submitted!
                    </p>
                  )}
                  {submitError && (
                    <p
                      style={{
                        fontSize: '14px',
                        color: '#FF2238',
                        margin: 0,
                      }}
                    >
                      Unexpected error occurred
                    </p>
                  )}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="asda__submit-button"
                style={{
                  marginLeft: isMobile ? '0' : 'auto',
                  flexShrink: 0,
                }}
              >
                {loading ? (
                  <div className="asda__button-spinner--wrapper">
                    <div className="asda__button-spinner" />
                  </div>
                ) : (
                  <p>Submit</p>
                )}
              </button>
            </div>

            {/* --- TURNSTILE WIDGET --- */}
            <div
              style={{
                marginTop: '26px',
                width: '100%',
                maxInlineSize: isMobile ? 'fit-content' : '',
              }}
            >
              <div
                style={{
                  marginBottom: '12px',
                  display: 'flex',
                  justifyContent: isMobile ? 'center' : 'flex-end',
                  width: '100%',
                }}
              >
                <div
                  className="cf-turnstile"
                  data-sitekey={process.env.REACT_APP_DATA_SITE_KEY}
                  data-callback="onTurnstileSuccess"
                  data-theme="dark"
                  data-appearance="interaction-only"
                />
              </div>

              {errors?.captcha && (
                <p
                  className="asda__error__message"
                  style={{
                    textAlign: isMobile ? 'center' : 'right',
                    marginTop: '-8px',
                    marginBottom: '12px',
                  }}
                >
                  {errors.captcha}
                </p>
              )}
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};
